/**
 * MyAdjustmentsPage — "Rregullimet e mia".
 *
 * Vetëm-lexim, për punonjësin. Kur bonuset, avancet e rimbursimet u bashkuan te
 * "Rregullimet e pagës", ajo faqe mori leje `payroll.view.company` — dhe
 * punonjësi i thjeshtë mbeti pa asnjë vend ku të shihte çka i është shtuar apo
 * hequr nga paga. Fletëpagesa tregon shumat, jo arsyet.
 *
 * Draft-et nuk vijnë fare nga serveri: një rregullim i pambaruar nuk është ende
 * asgjë dhe do të ngrinte pyetje pa përgjigje.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Chip, Stack, Alert, CircularProgress, Tooltip, TextField, MenuItem,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, IconButton,
} from '@mui/material';
import SavingsIcon from '@mui/icons-material/Savings';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import PageHeader from '../components/common/PageHeader';
import StatusChip from '../components/common/StatusChip';
import ConfirmDialog from '../components/common/ConfirmDialog';
import AdjustmentReviewDialog from '../components/payroll/AdjustmentReviewDialog';
import apiService, { authFetch } from '../services/apiServices';
import { MONTHS_SQ, periodLabel } from '../utils/period';
import { CATEGORY_META, REPAYMENT_METHODS, typeLabel } from '../config/payrollAdjustments';

const fmt = (n) => Number(n || 0).toLocaleString('sq-AL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('sq-AL') : '—');
const periodOf = (r) => (r.periodYear && r.periodMonth ? `${MONTHS_SQ[r.periodMonth - 1]} ${r.periodYear}` : '—');
const periodKeyOf = (r) => (r.periodYear && r.periodMonth ? `${r.periodYear}-${String(r.periodMonth).padStart(2, '0')}` : '');

export default function MyAdjustmentsPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [period,  setPeriod]  = useState('');
  const [askOpen, setAskOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [snack,   setSnack]   = useState({ open: false, msg: '', sev: 'success' });
  const [detail,  setDetail]  = useState(null);   // paneli i shqyrtimit, vetëm-lexim
  const [toDrop,  setToDrop]  = useState(null);   // kërkesa që tërhiqet
  const [dropping, setDropping] = useState(false);
  // Të dhënat e kompanisë i duhen kokës së vendimit në PDF — po ashtu si te
  // "Pagat e mia", ku fletëpagesa del me logon dhe NUI-n e punëdhënësit.
  const [company, setCompany] = useState({});
  const [req, setReq] = useState({
    amount: '', description: '',
    effectiveDate: new Date().toISOString().slice(0, 10),
    repaymentMethod: 'lump_sum', installments: 1,
  });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await apiService().get('my-adjustments');
      if (r.success) setRows(r.data || []);
      else { setRows([]); setError(r.errorMessage || 'Nuk u ngarkuan rregullimet.'); }
    } catch (e) {
      setRows([]); setError(e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    apiService().get('settings')
      .then((r) => { if (alive) setCompany(r?.data?.[0] || {}); })
      .catch(() => { /* pa cilësime, vendimi del pa logo — jo pa vendim */ });
    return () => { alive = false; };
  }, []);

  const periods = useMemo(() => {
    const keys = new Set(rows.map(periodKeyOf).filter(Boolean));
    return Array.from(keys).sort((a, b) => b.localeCompare(a)).map((k) => {
      const [y, m] = k.split('-');
      return { key: k, label: `${MONTHS_SQ[Number(m) - 1]} ${y}` };
    });
  }, [rows]);

  const filtered = useMemo(
    () => rows.filter((r) => !period || periodKeyOf(r) === period),
    [rows, period],
  );

  // Ndarë sipas drejtimit: një shifër e vetme që përzien shtesat me ndalesat
  // nuk do të thoshte asgjë për atë që e lexon.
  const totals = useMemo(() => filtered.reduce((a, r) => {
    if (r.status !== 'approved' && r.status !== 'processed') return a;
    const amt = Number(r.amount) || 0;
    if (r.category === 'addition')           a.additions += amt;
    else if (r.category === 'reimbursement') a.reimbursements += amt;
    else                                     a.deductions += amt;
    return a;
  }, { additions: 0, deductions: 0, reimbursements: 0 }), [filtered]);

  // Kërkesa shkon gjithnjë si 'pending' — statusin e vendos HR-ja. Serveri e
  // detyron këtë; këtu vetëm thuhet qartë, që pritja të mos duket refuzim.
  const canAsk = Number(req.amount) > 0 && Boolean(req.description.trim()) && Boolean(req.effectiveDate);
  const submitRequest = async () => {
    if (!canAsk) return;
    setSending(true);
    try {
      const r = await authFetch('/api/my-adjustments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...req, amount: parseFloat(req.amount) || 0 }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || 'Kërkesa nuk u dërgua.');
      setAskOpen(false);
      setReq((p) => ({ ...p, amount: '', description: '' }));
      setSnack({ open: true, msg: 'Kërkesa u dërgua — pret aprovimin e HR-së.', sev: 'success' });
      await load();
    } catch (e) {
      setSnack({ open: true, msg: e.message, sev: 'error' });
    } finally { setSending(false); }
  };

  // Tërheqja e kërkesës derisa asnjë vendim nuk është marrë. Pa këtë, një shumë
  // e shtypur gabim mund të hiqej vetëm duke e lypur dikë tjetër ta refuzonte —
  // dhe punonjësit i mbetej një "Refuzuar" për gabimin e vet.
  const withdraw = async () => {
    if (!toDrop) return;
    setDropping(true);
    try {
      const r = await authFetch(`/api/my-adjustments/${toDrop.id}/withdraw`, { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || 'Kërkesa nuk u tërhoq.');
      setToDrop(null);
      setSnack({ open: true, msg: 'Kërkesa u tërhoq.', sev: 'success' });
      await load();
    } catch (e) {
      setSnack({ open: true, msg: e.message, sev: 'error' });
    } finally { setDropping(false); }
  };

  /** Kërkesa e vetja, ende pa vendim — e vetmja që tërhiqet. */
  const canWithdraw = (r) => r.status === 'pending' && r.origin === 'employee';

  const thSx = {
    fontWeight: 700, fontSize: '0.68rem', py: 1, px: 1.25, color: '#6B7280',
    bgcolor: '#F9FAFB', textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap',
  };
  const tdSx = { py: 0.9, px: 1.25, fontSize: '0.8rem', borderBottom: '1px solid #F3F4F6' };

  return (
    <Box>
      <PageHeader
        title="Rregullimet e mia"
        subtitle="Shtesat, avancet, rimbursimet dhe ndalesat e regjistruara në emrin tuaj."
        actions={
          <Button variant="contained" size="small" disableElevation startIcon={<SavingsIcon />}
            onClick={() => setAskOpen(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Kërko avanc
          </Button>
        }
      />

      {periods.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <TextField select size="small" label="Periudha" value={period}
            onChange={(e) => setPeriod(e.target.value)} sx={{ width: 180 }}>
            <MenuItem value="">Të gjitha</MenuItem>
            {periods.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
          </TextField>
        </Stack>
      )}

      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
        {!loading && !error && filtered.length > 0 && (
          <Box sx={{ px: 2, py: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid #F3F4F6', bgcolor: '#FCFCFD' }}>
            {[
              { l: 'Shtesa',      v: totals.additions,      m: CATEGORY_META.addition },
              { l: 'Avanc + ndalesa', v: totals.deductions, m: CATEGORY_META.deduction },
              { l: 'Rimbursime',  v: totals.reimbursements, m: CATEGORY_META.reimbursement },
            ].map((c) => (
              <Box key={c.l} sx={{ flex: '1 1 150px', px: 1.25, py: 1, borderRadius: 1.5, bgcolor: c.m.soft, border: `1px solid ${c.m.border}` }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {c.l}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: c.m.color, lineHeight: 1.3 }}>
                  {c.m.sign}€{fmt(c.v)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>Kategoria</TableCell>
                <TableCell sx={thSx}>Lloji</TableCell>
                <TableCell sx={thSx}>Përshkrimi</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right' }}>Shuma</TableCell>
                <TableCell sx={thSx}>Periudha e pagës</TableCell>
                <TableCell sx={thSx}>Statusi</TableCell>
                <TableCell sx={thSx}>Data</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right', width: 90 }}>Veprimet</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}><CircularProgress size={24} /></TableCell></TableRow>
              )}
              {!loading && error && (
                <TableRow><TableCell colSpan={8} sx={{ py: 3, border: 0 }}>
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>{error}</Alert>
                </TableCell></TableRow>
              )}
              {!loading && !error && filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, border: 0, color: '#6B7280', fontSize: '0.85rem' }}>
                  Nuk ka rregullime{period ? ' për periudhën e zgjedhur' : ''}.
                </TableCell></TableRow>
              )}
              {!loading && !error && filtered.map((r) => {
                const m  = CATEGORY_META[r.category] || {};
                return (
                  <React.Fragment key={r.id}>
                  <TableRow hover sx={{ borderLeft: `3px solid ${m.bg}` }}>
                    <TableCell sx={tdSx}>
                      <Chip size="small" label={m.label}
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: m.bg, color: m.color }} />
                    </TableCell>
                    <TableCell sx={tdSx}>{typeLabel(r.category, r.type)}</TableCell>
                    <TableCell sx={{ ...tdSx, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6B7280' }}>
                      <Tooltip title={r.description || ''}><span>{r.description || '—'}</span></Tooltip>
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: m.color, whiteSpace: 'nowrap' }}>
                        {m.sign}€{fmt(r.amount)}
                      </Typography>
                      {r.category === 'advance' && r.remainingBalance != null && (
                        <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF' }}>
                          mbetur €{fmt(r.remainingBalance)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap', color: '#4B5563' }}>{periodOf(r)}</TableCell>
                    <TableCell sx={tdSx}>
                      <StatusChip status={r.status} size="sm" />
                    </TableCell>
                    <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap', color: '#6B7280' }}>{fmtDate(r.effectiveDate)}</TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Shiko rrugën e kërkesës">
                        <IconButton size="small" onClick={() => setDetail(r)}>
                          <FactCheckOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canWithdraw(r) && (
                        <Tooltip title="Tërhiq kërkesën">
                          <IconButton size="small" color="warning" onClick={() => setToDrop(r)}>
                            <UndoRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Arsyeja e refuzimit rri nën rreshtin e vet, e shkruar plot.
                      Më parë punonjësi lexonte vetëm "Refuzuar" — statusi thoshte
                      çka ndodhi, kurrë pse. */}
                  {r.status === 'rejected' && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0.75, px: 1.25, borderBottom: '1px solid #F3F4F6', bgcolor: '#FEF2F2' }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#991B1B' }}>
                          <strong>Arsyeja e refuzimit:</strong>{' '}
                          {r.rejectionReason || 'nuk është regjistruar — pyet Burimet Njerëzore.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 1, display: 'block' }}>
        Vetëm rregullimet e aprovuara hyjnë në përllogaritjen e pagës. Për pyetje, drejtohuni te Burimet Njerëzore.
      </Typography>

      {/* ── Kërkesë për avanc ── */}
      <Dialog open={askOpen} onClose={() => !sending && setAskOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>Kërko avanc</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              Kërkesa shkon te Burimet Njerëzore për aprovim. Pas aprovimit, shuma
              zbritet nga paga e muajit përkatës.
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField size="small" label="Shuma (€)" required type="number" sx={{ flex: 1 }}
                inputProps={{ min: 0.01, step: 0.01 }}
                value={req.amount} onChange={(e) => setReq((p) => ({ ...p, amount: e.target.value }))} />
              <TextField size="small" label="Data" required type="date" sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
                helperText={req.effectiveDate ? `Paga e ${periodLabel(req.effectiveDate)}` : ' '}
                value={req.effectiveDate} onChange={(e) => setReq((p) => ({ ...p, effectiveDate: e.target.value }))} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField select size="small" label="Mënyra e kthimit" sx={{ flex: 1 }}
                value={req.repaymentMethod} onChange={(e) => setReq((p) => ({ ...p, repaymentMethod: e.target.value }))}>
                {REPAYMENT_METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Këste" type="number" sx={{ width: 100 }}
                inputProps={{ min: 1, max: 60 }}
                disabled={req.repaymentMethod !== 'installments'}
                value={req.installments} onChange={(e) => setReq((p) => ({ ...p, installments: Number(e.target.value) }))} />
            </Stack>
            <TextField size="small" label="Arsyeja" required multiline rows={2}
              value={req.description} onChange={(e) => setReq((p) => ({ ...p, description: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setAskOpen(false)} disabled={sending} sx={{ textTransform: 'none', fontWeight: 700 }}>Anulo</Button>
          <Button variant="contained" disableElevation onClick={submitRequest} disabled={sending || !canAsk}
            startIcon={sending ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            {sending ? 'Duke dërguar…' : 'Dërgo kërkesën'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* I njëjti panel si te HR-ja, pa `actor` — pra pa asnjë buton vendimi:
          punonjësi lexon rrugën e kërkesës (kërkuar → dërguar → vendim, me
          arsyen), kurse vendimin e merr HR-ja. Një komponent, një kronologji. */}
      <AdjustmentReviewDialog
        open={Boolean(detail)}
        row={detail}
        actor={{}}
        company={company}
        onClose={() => setDetail(null)}
        onNotify={(msg, sev) => setSnack({ open: true, msg, sev: sev || 'success' })}
      />

      <ConfirmDialog
        open={Boolean(toDrop)} busy={dropping}
        title="Tërhiq kërkesën"
        message="Kërkesa hiqet nga radha e shqyrtimit dhe nuk shfaqet më. Mund të dërgohet një e re kurdo."
        confirmLabel="Tërhiq" busyLabel="Duke tërhequr…" color="warning"
        onConfirm={withdraw} onClose={() => setToDrop(null)}
        details={toDrop && (
          <Stack spacing={0.75}>
            {[['Shuma', `€${fmt(toDrop.amount)}`],
              ['Periudha', periodOf(toDrop)],
              ['Arsyeja', toDrop.description || '—']].map(([k, v]) => (
              <Stack key={k} direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="body2" color="text.secondary">{k}</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right' }}>{v}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      />

      <Snackbar open={snack.open} autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
